import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { alertError, alertSuccess } from "../../../utils/logger";
import LoadingDialog from "./LoadingDialog";
import { postPartida } from "../../../api/partida";
import { PARTIDA_REFERENCE_MAX_LENGTH } from "../../../utils/constants";
import { getCustomers } from "../../../api/customer";

function getEmptyPlantillaPartida() {
  return {
    referencia: null,
    descripcion: null,
    categoria_id: null,
    categoria_ids: [],
    concepto: null,
    cliente_id: null,
    productos: [],
    agrupaciones_materiales: [],
  };
}

function simplifyObject(input) {
  const safe = input || {};

  return {
    referencia: safe.referencia ?? null,
    descripcion: safe.descripcion ?? null,
    // En oferta viene como categoria_id, en plantillas suele venir categoria.id
    categoria_id: safe.categoria_id ?? safe.categoria?.id ?? null,
    categoria_ids: safe.categoria_ids ?? safe.categorias?.map((categoria) => categoria.id) ?? [safe.categoria_id ?? safe.categoria?.id].filter(Boolean),
    concepto: safe.concepto ?? null,
    cliente_id: safe.cliente_id ?? null,
    productos: (safe.productos ?? [])
      .map((producto) => ({
        producto_id:
          producto.plantilla_producto_original_id ?? // caso Oferta
          producto.producto_id ??                    // caso Plantilla plana
          producto.producto?.id ??                   // caso Plantilla detalle 
          null,
        unidades: producto.def_unidades ?? producto.unidades ?? 1,
      }))
      .filter((p) => p.producto_id),
    agrupaciones_materiales: (safe.agrupaciones_materiales ?? []).map((group) => ({
      agrupacion_material_original_id: group.agrupacion_material_original_id,
      referencia: group.referencia,
      descripcion: group.descripcion,
      tipo_unidad_id: group.tipo_unidad_id,
      unidades: group.unidades ?? 1,
      ordinal: group.ordinal ?? 0,
      productos: (group.productos ?? []).map((product, ordinal) => ({
        producto_id: product.plantilla_producto_original_id ?? product.producto_id,
        unidades: (product.def_unidades ?? product.unidades ?? 1) / (group.unidades || 1),
        ordinal,
      })).filter((product) => product.producto_id),
    })),
  };
}

const GuardarPlantillaPartidaDialog = (props) => {
  const { title, open, setOpen, data, onSave, defaultCustomerId = null } = props;

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  const [plantillaPartida, setPlantillaPartida] = useState({
    ...getEmptyPlantillaPartida(),
    ...simplifyObject(data || {}),
  });

  useEffect(() => {
    if (open)
      setPlantillaPartida({
        ...getEmptyPlantillaPartida(),
        ...simplifyObject(data),
        // Desde una oferta se propone su cliente; el usuario puede cambiarlo a General.
        cliente_id: defaultCustomerId || null,
      });
  }, [open, data, defaultCustomerId]);

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );

  useEffect(() => {
    if (!open) return;

    getCustomers(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setCustomers(
            (res.data || []).filter(
              (customer) => customer?.activo && customer?.usar_partidas_especificas
            )
          );
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se han podido obtener los clientes disponibles.",
        });
      });
  }, [open, loggedUser?.accessToken]);

  function savePartida() {
    const validatePartida = () => {
      let error = "";

      if (!plantillaPartida?.referencia) {
        error = "La referencia es obligatoria.";
      } else if (
        plantillaPartida.referencia.length > PARTIDA_REFERENCE_MAX_LENGTH
      ) {
        error = `La referencia no puede superar los ${PARTIDA_REFERENCE_MAX_LENGTH} caracteres.`;
      }

      if (error) {
        alertError({
          error: error,
        });
        return false;
      }

      return true;
    };

    if (!validatePartida()) {
      return;
    }
    console.log("PAYLOAD postPartida:", plantillaPartida);
    postPartida(plantillaPartida, loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 201) {
          alertSuccess({
            title: "¡Guardada!",
            customMessage: "Partida guardada con éxito.",
          });
          setOpen(false);
          setPlantillaPartida(getEmptyPlantillaPartida());
          if (typeof onSave === "function") {
            onSave({
              id: res.data?.id,
              referencia: plantillaPartida?.referencia ?? null,
              descripcion: plantillaPartida?.descripcion ?? null,
            });
          }
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se ha podido guardar la partida.",
        });
      });
  }

  const handleChange = (element) => (event) => {
    setPlantillaPartida({ ...plantillaPartida, [element]: event.target.value });
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="table-dialog"
        className="backdrop"
      >
        <DialogTitle id="table-dialog">{title}</DialogTitle>
        <DialogContent>
          <div className="row d-flex align-items-center">
            <div className="col-md-4">
              <TextField
                fullWidth
                label="Referencia"
                value={plantillaPartida.referencia || ""}
                onChange={handleChange("referencia")}
                error={
                  (plantillaPartida?.referencia?.length || 0) >
                  PARTIDA_REFERENCE_MAX_LENGTH
                }
                helperText={
                  (plantillaPartida?.referencia?.length || 0) >
                  PARTIDA_REFERENCE_MAX_LENGTH
                    ? `Máximo ${PARTIDA_REFERENCE_MAX_LENGTH} caracteres.`
                    : ""
                }
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                required
              />
            </div>

            <div className="col-md-8">
              <TextField
                fullWidth
                label="Descripción"
                value={plantillaPartida.descripcion || ""}
                onChange={handleChange("descripcion")}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                multiline
                minRows={2}
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                select
                label="Asociar a un cliente"
                value={plantillaPartida.cliente_id || ""}
                onChange={(event) =>
                  setPlantillaPartida({
                    ...plantillaPartida,
                    cliente_id: event.target.value || null,
                  })
                }
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selectedCustomerId) => {
                    if (!selectedCustomerId) return "General";
                    return (
                      customers.find(
                        (customer) => customer.id === selectedCustomerId
                      )?.nombre || "General"
                    );
                  },
                }}
                margin="normal"
                variant="outlined"
                helperText={
                  defaultCustomerId
                    ? "Se propone el cliente de la oferta. Puede cambiarlo a General."
                    : "General estará disponible para todos los clientes."
                }
              >
                <MenuItem value="">General</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="mr-2"
            onClick={() => {
              setOpen(false);
              setPlantillaPartida(getEmptyPlantillaPartida());
            }}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              savePartida();
            }}
            variant="outlined"
            color="primary"
          >
            Guardar
          </Button>
          <br />
        </DialogActions>
      </Dialog>
      <LoadingDialog open={loading} />
    </>
  );
};
export default GuardarPlantillaPartidaDialog;
